"use client";

import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSignIn, useVerifyBackupCode } from "@/hooks/auth";
import { authClient } from "@/db/auth/client";
import { useSearchParams, useRouter } from "next/navigation";
import { Suspense } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@garagem/ui/components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@garagem/ui/components/form";
import { Input } from "@garagem/ui/components/input";
import { PasswordInput } from "@garagem/ui/components/password-input";
import { Button } from "@garagem/ui/components/button";
import { Separator } from "@garagem/ui/components/separator";
import Link from "next/link";
import { toast } from "@garagem/ui/components/sonner";
import { ArrowLeft, Shield, Smartphone } from "lucide-react";
import { classifyEmailSignInResult } from "@/lib/auth/email-sign-in-result";
import { authRedirectWithSession, getReturnUrl } from "@/lib/auth/redirect";
import { authPathWithSearch } from "@web/lib/auth/strip-credential-search-params";
import { shouldShowOperatorGoogleLogin } from "@/lib/auth/trusted-web-origin";
import { captureException } from "@web/lib/analytics/posthog-client";
import { AccessPolicyNotice } from "./access-policy-notice";
import { ACCESS_POLICY_NETWORK_DENIED_CODE } from "@garagem/foundation-shared/domain/auth/access-policy/notices";

// Define the form schemas using Zod
const signInSchema = z.object({
  email: z.email("Email inválido"),
  password: z.string().min(1, "Senha é obrigatória"),
});

type SignInFormData = z.infer<typeof signInSchema>;

interface AuthErrorLike {
  code?: string;
  message?: string;
  error?: { code?: string; message?: string };
}

function asAuthError(err: unknown): AuthErrorLike {
  return (typeof err === "object" && err !== null ? err : {}) as AuthErrorLike;
}

// Error code to message mapping
const ERROR_MESSAGES = {
  INVALID_PASSWORD: {
    title: "Email ou senha inválidos",
    description: "Verifique suas credenciais e tente novamente.",
  },
  INVALID_EMAIL_OR_PASSWORD: {
    title: "Email ou senha inválidos",
    description: "Verifique suas credenciais e tente novamente.",
  },
  USER_BANNED: {
    title: "Acesso bloqueado",
    description: "Seu acesso está bloqueado, entre em contato com o suporte pelo email contato@garagem.ai",
  },
  EMAIL_NOT_VERIFIED: {
    title: "Email não verificado",
    description: "Verifique seu email antes de fazer login.",
  },
  ACCOUNT_LOCKED: {
    title: "Conta bloqueada",
    description: "Sua conta foi temporariamente bloqueada.",
  },
  INVALID_OTP: {
    title: "Código inválido",
    description: "Verifique o código e tente novamente.",
  },
  INVALID_TOTP: {
    title: "Código inválido",
    description: "Verifique o código e tente novamente.",
  },
  EXPIRED_OTP: {
    title: "Código expirado",
    description: "O código expirou, gere um novo código.",
  },
  INVALID_BACKUP_CODE: {
    title: "Código de backup inválido",
    description: "Verifique o código e tente novamente.",
  },
  [ACCESS_POLICY_NETWORK_DENIED_CODE]: {
    title: "Acesso só de redes conhecidas",
    description:
      "Sua imobiliária só permite o acesso de certos lugares. Se você está no escritório e mesmo assim não entra, fale com o gestor.",
  },
} as const;

function SignInFormInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  // React Query hooks
  const signIn = useSignIn();
  const verifyBackupCode = useVerifyBackupCode();

  // Get email and return URL from URL params
  const emailFromUrl = searchParams?.get("email");
  const returnUrl = getReturnUrl(searchParams || new URLSearchParams());
  const isInvitationFlow = !!(
    emailFromUrl && searchParams?.get("from")?.includes("aceitar-convite")
  );
  // Operator Google SSO: ?admin=true or the Pilot OAuth flow.
  const showGoogleLogin = shouldShowOperatorGoogleLogin(
    searchParams ?? new URLSearchParams()
  );
  const authError = searchParams?.get("erro");

  useEffect(() => {
    if (authError === "conta_nao_permitida") {
      toast.error("Conta não permitida", {
        description: "Login não autorizado. Entre em contato com o suporte.",
      });
    } else if (authError === "autenticacao") {
      toast.error("Erro de autenticação", {
        description: "Ocorreu um erro ao fazer login. Tente novamente.",
      });
    }
  }, [authError]);

  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: emailFromUrl || "",
      password: "",
    },
  });

  // Update form when email param changes
  useEffect(() => {
    if (emailFromUrl) {
      signInForm.setValue("email", emailFromUrl);
    }
  }, [emailFromUrl, signInForm]);

  // Handle successful authentication redirect
  const handleSuccessfulAuth = useCallback(async () => {
    toast.success("Login realizado com sucesso!", {
      description: "Redirecionando...",
    });

    // Use the new redirect utility that waits for session
    await authRedirectWithSession(returnUrl);
  }, [returnUrl]);

  // Handle authentication errors
  const handleAuthError = useCallback((error: unknown) => {
    const authError = asAuthError(error);
    const errorCode = authError.code;
    const errorMessage = authError.message?.toLowerCase() || "";
    
    // Check if user is banned - check both code and message
    const isBanned = 
      errorCode === "USER_BANNED" ||
      errorMessage.includes("banned") ||
      errorMessage.includes("bloqueado") ||
      errorMessage.includes("blocked");
    
    // Redirect banned users to banned page
    if (isBanned) {
      router.replace("/acesso-bloqueado");
      return;
    }

    const errorConfig =
      ERROR_MESSAGES[errorCode as keyof typeof ERROR_MESSAGES];

    if (errorConfig) {
      toast.error(errorConfig.title, {
        description: errorConfig.description,
      });
    } else {
      // Unclassified sign-in failure — capture it so these stop being invisible
      // in error tracking (classified auth errors are expected, not captured).
      captureException(error, {
        source: "sign-in-form",
        component: "SignInForm",
        action: "sign-in",
        errorCode: errorCode ?? "unknown",
      });
      toast.error("Erro ao fazer login", {
        description:
          authError.message ||
          "Ocorreu um erro ao fazer login. Tente novamente.",
      });
    }
  }, []);

  const handleSignIn = async (formData: SignInFormData) => {
    try {
      const data = await signIn.mutateAsync({
        email: formData.email,
        password: formData.password,
      });

      const outcome = classifyEmailSignInResult(data);

      if (outcome.kind === "two-factor") {
        setUserEmail(formData.email);
        setShowTwoFactor(true);
        setVerificationCode("");
        toast.info("2FA necessário", {
          description: "Digite o código do seu aplicativo autenticador",
        });
        return;
      }

      if (outcome.kind === "session") {
        await handleSuccessfulAuth();
        return;
      }

      // Authorize-page sign-in: the session cookie is set, and the body is
      // only the continuation URL (consent or the client's redirect URI).
      if (outcome.kind === "oauth-redirect") {
        window.location.href = outcome.url;
        return;
      }

      throw new Error("Resposta inválida do servidor");
    } catch (err: unknown) {
      const authError = asAuthError(err);
      const errorCode = authError.code || authError.error?.code;
      const errorMessage = (
        authError.message ||
        authError.error?.message ||
        ""
      ).toLowerCase();

      // Email-not-verified must be checked BEFORE the ban classifier, because
      // Better Auth returns status=403 for unverified emails. A blanket
      // "status===403 => banned" check would misroute these users to
      // /acesso-bloqueado instead of /aguardando-confirmacao.
      if (
        errorCode === "EMAIL_NOT_VERIFIED" ||
        errorMessage.includes("email not verified") ||
        errorMessage.includes("verify your email")
      ) {
        toast.error("Email não verificado", {
          description: "Você precisa verificar seu email antes de fazer login.",
        });

        const confirmationUrl = new URL("/aguardando-confirmacao", window.location.origin);
        confirmationUrl.searchParams.set("email", formData.email);

        if (returnUrl !== "/") {
          confirmationUrl.searchParams.set("returnUrl", returnUrl);
        }

        window.location.href = confirmationUrl.toString();
        return;
      }

      // Only treat as banned when the signal is explicit. Do NOT key off bare
      // status=403 — Better Auth uses 403 for several "forbidden but not
      // banned" cases (unverified email, disabled account, etc).
      const isBanned =
        errorCode === "USER_BANNED" ||
        errorCode === "BANNED" ||
        errorMessage.includes("banned") ||
        errorMessage.includes("acesso bloqueado") ||
        errorMessage.includes("contato@garagem.ai");

      if (isBanned) {
        router.replace("/acesso-bloqueado");
        return;
      }

      handleAuthError(err);
    }
  };

  const handleTwoFactorVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // For 2FA during login, we need to use the authClient directly
      // since the useVerifyTOTP hook expects a password parameter for setup
      const { data: _data, error } = await authClient.twoFactor.verifyTotp({
        code: verificationCode,
      });

      if (error) {
        throw error;
      }

      // Reset 2FA state and redirect
      setShowTwoFactor(false);
      setUserEmail("");
      setVerificationCode("");

      toast.success("Verificação 2FA concluída!", {
        description: "Login realizado com sucesso.",
      });

      await handleSuccessfulAuth();
    } catch (err: unknown) {
      handleAuthError(err);
    }
  };

  const handleBackupCodeVerification = async (backupCode: string) => {
    try {
      await verifyBackupCode.mutateAsync({ code: backupCode });

      // Reset 2FA state and redirect
      setShowTwoFactor(false);
      setUserEmail("");
      setVerificationCode("");

      toast.success("Código de backup aceito!", {
        description: "Login realizado com sucesso.",
      });

      await handleSuccessfulAuth();
    } catch (err: unknown) {
      handleAuthError(err);
    }
  };

  const resetToSignIn = useCallback(() => {
    setShowTwoFactor(false);
    setUserEmail("");
    setVerificationCode("");
    signInForm.reset();
  }, [signInForm]);

  // Cleanup effect to prevent stuck states
  useEffect(() => () => {
      setShowTwoFactor(false);
      setUserEmail("");
      setVerificationCode("");
    }, []);

  // Handle 2FA timeout
  useEffect(() => {
    if (!showTwoFactor || signIn.isPending || verifyBackupCode.isPending)
      return;

    const timer = setTimeout(
      () => {
        toast.info("Sessão expirada", {
          description: "Faça login novamente.",
        });
        resetToSignIn();
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearTimeout(timer);
  }, [
    showTwoFactor,
    signIn.isPending,
    verifyBackupCode.isPending,
    resetToSignIn,
  ]);

  // Loading state from any of the mutations
  const isLoading = signIn.isPending || verifyBackupCode.isPending;

  if (showTwoFactor) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToSignIn}
              disabled={isLoading}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-xl">Verificação 2FA</CardTitle>
            </div>
          </div>
          <CardDescription>
            Digite o código de 6 dígitos do seu aplicativo autenticador para{" "}
            {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            method="post"
            onSubmit={handleTwoFactorVerification}
            className="space-y-4"
          >
            <div className="space-y-2">
              <label
                htmlFor="verification-code"
                className="text-sm font-medium"
              >
                Código de verificação
              </label>
              <Input
                id="verification-code"
                value={verificationCode}
                onChange={(e) => {
                  // Only allow numbers and limit to 6 digits
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setVerificationCode(value);
                }}
                placeholder="000000"
                maxLength={6}
                className="text-center text-lg font-mono tracking-widest"
                disabled={isLoading}
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || verificationCode.length !== 6}
              className="w-full"
            >
              {isLoading ? "Verificando..." : "Verificar"}
            </Button>
          </form>

          <Separator className="my-4" />

          {/* Backup Code Section */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              Problemas com o aplicativo autenticador?
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                const backupCode = prompt(
                  "Digite seu código de backup de 10 caracteres:"
                );
                if (backupCode) {
                  handleBackupCodeVerification(backupCode.trim());
                }
              }}
              disabled={isLoading}
            >
              Usar código de backup
            </Button>
          </div>
        </CardContent>
        <CardFooter>
          <div className="text-center text-xs text-muted-foreground w-full">
            <Smartphone className="h-4 w-4 inline mr-1" />O código muda a cada
            30 segundos
          </div>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Entrar</CardTitle>
        <CardDescription>
          Entre com seu email e senha para acessar sua conta
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AccessPolicyNotice />
        <Form {...signInForm}>
          <form
            // HTML default is GET. Native submit (pre-hydration Enter,
            // password-manager autofill) would put name=password in the
            // query string — method=post keeps it in the body instead.
            method="post"
            onSubmit={signInForm.handleSubmit(handleSignIn)}
            className="space-y-4"
          >
            <FormField
              control={signInForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="username"
                      placeholder="seu@email.com"
                      disabled={isLoading || isInvitationFlow}
                      className={isInvitationFlow ? "bg-muted" : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signInForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Senha</FormLabel>
                    <Link href="/esqueci-senha" className="text-sm underline">
                      Esqueceu sua senha?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="current-password"
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </Form>

        {showGoogleLogin && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              disabled={isLoading}
              onClick={async () => {
                await authClient.signIn.social({
                  provider: "google",
                  callbackURL: returnUrl,
                });
              }}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Entrar com Google
            </Button>
          </>
        )}
      </CardContent>
      <CardFooter>
        <div className="text-center text-sm w-full">
          Não tem uma conta?{" "}
          <Link
            href={authPathWithSearch("/cadastrar", searchParams)}
            className="underline"
          >
            Criar conta
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
}

export function SignInForm() {
  return (
    <Suspense fallback={null}>
      <SignInFormInner />
    </Suspense>
  );
}
